import React, { useState, useEffect, useRef } from "react";
import TaskmanagerImage from "./images/Taskmanager.JPG";
import VisualiserImage from "./images/visualiser.JPG";
import FaucetImage from "./images/faucetimage.JPG";
import GameImage from "./images/gameimage.JPG";

const projects = [
  {
    title: "Project : Blockchain Visualiser",
    description: "A blockchain visualisation dashboard that displays real-time Monad network data including latest blocks, transactions, TPS (Transactions Per Second), and gas metrics. Built with HTML, CSS, and JavaScript to provide an animated interface for monitoring blockchain activity. This was one of the featured projects by the Monad Developer Community for  Mission 4",
    liveUrl: "https://monad-visualiser.vercel.app/",
    githubUrl: "https://github.com/Pjewels/Visualiser-frontend",
    image: VisualiserImage
  },
  {
    title: "Project : Nadsequence Game",
    description: "A digital version of the popular simon says game that integrates Monad Games ID to submit and record player scores on a global leaderboard hosted on Monad blockchain. This project was one of the featured games on MonadClip, a gaming competition organized by Monad Developer Relations, showcasing innovative Web3 gaming integration.",
    liveUrl: "https://nadsequence.xyz",
    githubUrl: "https://github.com/Pjewels/NadSequence",
    image: GameImage
  },
  {
    title: "Project : Task Manager",
    description: "A Task manager app that allows you to add, edit, and delete tasks. It also allows you to filter tasks by status and priority etc. This project is built with Vue, JavaScript, HTML, and Tailwind CSS.",
    liveUrl: "https://task-manager-pricelez.vercel.app",
    githubUrl: "https://github.com/Pjewels/Task-manager",
    image: TaskmanagerImage
  },
  {
    title: "Project : Crypto Faucet",
    description: "A simple, secure and user-friendly faucet for Monad testnet. It uses a simple, beautiful UI and is built with Node.js, Express.js, and ethers.js.",
    liveUrl: "https://dropfi-faucet.vercel.app/",
    githubUrl: "https://github.com/Pjewels/DropFiFaucet",
    image: FaucetImage
  }
];

const Projects: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.2,
        rootMargin: "-50px"
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="projects"
      className="px-4 md:px-16 py-16 mt-32"
    >
      <h3
        className={`text-2xl font-mono text-center mb-6 transform transition-all duration-700 ${
          isVisible ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0"
        }`}
      >
        &gt; Projects
      </h3>

      <div
        className={`text-center mb-16 font-mono text-gray-400 transform transition-all duration-700 delay-200 ${
          isVisible ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
        }`}
      >
        <span className="text-red-400">&lt;div&gt;</span>
        Here are some of the projects I've worked on, each focused on solving problems and
        delivering excellent user experiences.
        <span className="text-red-400">&lt;/div&gt;</span>
      </div>

      <div className="space-y-40 max-w-5xl mx-auto">
        {projects.map((project, index) => (
          <div
            key={project.title + index}
            className={`flex flex-col md:flex-row items-start gap-8 transform transition-all duration-700 ${
              isVisible 
                ? "translate-x-0 opacity-100" 
                : (index % 2 === 0 ? "-translate-x-full" : "translate-x-full") + " opacity-0"
            }`}
            style={{ transitionDelay: `${(index + 2) * 200}ms` }}
          >
            {/* Project Info */}
            <div className="flex-1 space-y-4">
              <h4 className="text-xl font-mono">
                {project.title}
              </h4>
              <p className="text-gray-400 font-mono text-sm">
                {project.description}
              </p>
              <div className="flex gap-4 pt-4">
                <a
                  href={project.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-1.5 bg-white text-black rounded-full font-mono text-sm hover:bg-gray-200 transition-colors"
                >
                  Live Preview
                </a>
                <a
                  href={project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-1.5 border border-white/20 rounded-full font-mono text-sm hover:bg-white/10 transition-colors"
                >
                  Check Github
                </a>
              </div>
            </div>

            {/* Project Image */}
            <div className="flex-1 w-full md:w-auto">
              <div className="relative group">
                <div className="relative overflow-hidden rounded-lg border border-white/10">
                  <div className="aspect-video bg-gray-900/50 backdrop-blur-sm">
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                  </div>
                  {/* Reflection effect */}
                  <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-white/10 backdrop-blur-sm transform translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Projects;